import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getGlobalStats, getDepartmentStats, getFormsPendingApproval } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Users, Building2, CheckSquare, Clock, TrendingUp, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const Dashboard = () => {
  const { user, isSuperAdmin, isDepartmentHead } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingForms, setPendingForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week');

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      if (isSuperAdmin()) {
        const globalData = await getGlobalStats(timeRange);
        setStats({ global: globalData });
      } else if (isDepartmentHead()) {
        const deptData = await getDepartmentStats(timeRange);
        setStats({ department: deptData });
      }
      
      const pending = await getFormsPendingApproval();
      setPendingForms(pending);
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  const globalStats = stats?.global;
  const deptStats = stats?.department;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Willkommen zurück, {user?.full_name || user?.username}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant={timeRange === 'today' ? 'default' : 'outline'} onClick={() => setTimeRange('today')}>Heute</Button>
          <Button size="sm" variant={timeRange === 'week' ? 'default' : 'outline'} onClick={() => setTimeRange('week')}>Woche</Button>
          <Button size="sm" variant={timeRange === 'month' ? 'default' : 'outline'} onClick={() => setTimeRange('month')}>Monat</Button>
        </div>
      </div>

      {/* Super Admin Global Stats */}
      {isSuperAdmin() && globalStats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Aktive Formulare</CardTitle>
                <FileText className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{globalStats.active_forms || 0}</div>
                <p className="text-xs text-gray-500 mt-1">von {globalStats.total_forms} gesamt</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-emerald-500">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Einreichungen Heute</CardTitle>
                <TrendingUp className="h-4 w-4 text-emerald-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{globalStats.submissions_today || 0}</div>
                <p className="text-xs text-gray-500 mt-1">{globalStats.total_submissions} insgesamt</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-amber-500">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Wartende Genehmigungen</CardTitle>
                <Clock className="h-4 w-4 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{pendingForms?.length || 0}</div>
                <Link to="/admin/approvals">
                  <p className="text-xs text-blue-600 mt-1 hover:underline">Alle anzeigen →</p>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-purple-500">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Aktive Abteilungen</CardTitle>
                <Building2 className="h-4 w-4 text-purple-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{globalStats.active_departments || globalStats.total_departments || 0}</div>
                <Link to="/admin/departments">
                  <p className="text-xs text-blue-600 mt-1 hover:underline">Verwalten →</p>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card className="bg-gradient-to-r from-slate-50 to-blue-50 border-slate-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-700">Schnellaktionen</h3>
                  <p className="text-sm text-slate-500">Häufig verwendete Funktionen</p>
                </div>
                <div className="flex gap-2">
                  <Link to="/admin/forms/new">
                    <Button size="sm" variant="default">+ Neues Formular</Button>
                  </Link>
                  <Link to="/admin/submissions">
                    <Button size="sm" variant="outline">Einreichungen</Button>
                  </Link>
                  <Link to="/admin/users">
                    <Button size="sm" variant="outline">Benutzer</Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Original stats cards below */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Gesamt Formulare</CardTitle>
                <FileText className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{globalStats.total_forms}</div>
                <p className="text-xs text-gray-500 mt-1">{globalStats.active_forms} aktiv</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Einreichungen</CardTitle>
                <CheckSquare className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{globalStats.total_submissions}</div>
                <p className="text-xs text-green-600 mt-1">+{globalStats.submissions_today} heute</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Abteilungen</CardTitle>
                <Building2 className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{globalStats.total_departments}</div>
                <p className="text-xs text-gray-500 mt-1">{globalStats.active_departments} aktiv</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Benutzer</CardTitle>
                <Users className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{globalStats.total_users}</div>
                <p className="text-xs text-gray-500 mt-1">Systemweit</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Submissions Trend */}
            {globalStats.submissions_trend && globalStats.submissions_trend.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Einreichungen Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={globalStats.submissions_trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="count" stroke="#3B82F6" name="Einreichungen" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Status Distribution */}
            {globalStats.status_distribution && globalStats.status_distribution.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Status Verteilung</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={globalStats.status_distribution} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label>
                        {globalStats.status_distribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Top Forms */}
          {globalStats.top_forms && globalStats.top_forms.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Formulare</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={globalStats.top_forms}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#3B82F6" name="Einreichungen" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Top Departments */}
          {globalStats.top_departments && globalStats.top_departments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Abteilungen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {globalStats.top_departments.map((dept, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">{idx + 1}</div>
                        <div>
                          <p className="font-medium">{dept.name}</p>
                          <p className="text-xs text-gray-500">{dept.submission_count} Einreichungen</p>
                        </div>
                      </div>
                      <TrendingUp className="h-5 w-5 text-green-500" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Department Head Stats */}
      {isDepartmentHead() && deptStats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Meine Formulare</CardTitle>
                <FileText className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{deptStats.total_forms}</div>
                <p className="text-xs text-gray-500 mt-1">{deptStats.active_forms} aktiv</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Einreichungen</CardTitle>
                <CheckSquare className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{deptStats.total_submissions}</div>
                <p className="text-xs text-green-600 mt-1">+{deptStats.submissions_today} heute</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Ausstehend</CardTitle>
                <AlertCircle className="h-4 w-4 text-orange-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{pendingForms.length}</div>
                <p className="text-xs text-gray-500 mt-1">Genehmigungen</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Ø Bearbeitung</CardTitle>
                <Clock className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{deptStats.avg_processing_hours || 0}h</div>
                <p className="text-xs text-gray-500 mt-1">Durchschnitt</p>
              </CardContent>
            </Card>
          </div>

          {deptStats.submissions_trend && deptStats.submissions_trend.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Einreichungen Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={deptStats.submissions_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#3B82F6" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Pending Approvals */}
      {pendingForms.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-500" />
                <CardTitle>Genehmigungen ausstehend</CardTitle>
              </div>
              <Link to="/admin/approvals">
                <Button variant="outline" size="sm">Alle anzeigen</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingForms.slice(0, 5).map((form) => (
                <div key={form.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                  <div>
                    <h4 className="font-medium text-gray-900">{form.name}</h4>
                    <p className="text-sm text-gray-500">Von {form.created_by_name} · {form.department_name}</p>
                  </div>
                  <Link to="/admin/approvals">
                    <Button size="sm">Prüfen</Button>
                  </Link>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Schnellzugriff</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/admin/forms/new">
              <Button variant="outline" className="w-full h-20">
                <div className="flex flex-col items-center gap-2">
                  <FileText className="h-5 w-5" />
                  <span className="text-sm">Neues Formular</span>
                </div>
              </Button>
            </Link>
            <Link to="/admin/forms">
              <Button variant="outline" className="w-full h-20">
                <div className="flex flex-col items-center gap-2">
                  <FileText className="h-5 w-5" />
                  <span className="text-sm">Alle Formulare</span>
                </div>
              </Button>
            </Link>
            <Link to="/admin/submissions">
              <Button variant="outline" className="w-full h-20">
                <div className="flex flex-col items-center gap-2">
                  <CheckSquare className="h-5 w-5" />
                  <span className="text-sm">Einreichungen</span>
                </div>
              </Button>
            </Link>
            {isSuperAdmin() && (
              <Link to="/admin/users">
                <Button variant="outline" className="w-full h-20">
                  <div className="flex flex-col items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span className="text-sm">Benutzer</span>
                  </div>
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;